import type { RoleKey } from "@ondo/shared";

import { AGENT_PORTAL } from "../agent/agent.constants.ts";
import { COLLECTOR_PORTAL } from "../collector/collector.constants.ts";
import { ESTATE_PORTAL } from "../estate/estate.constants.ts";
import { GALLERY_PORTAL } from "../gallery/gallery.constants.ts";
import { VERIFIER_PORTAL } from "../verifier/verifier.constants.ts";
import type { PortalSpec } from "./portal.ts";

/**
 * Which portal a role opens, or none.
 *
 * The one place that answers it. `resolveWorkspaceRoute` used to carry a branch
 * per role — a bespoke one for the agent, a general one for the collector, and a
 * fall-through for the rest — which is three places to update when a fourth
 * portal lands and three chances to disagree about what a URL means.
 *
 * `Partial`, still, though every role now has an entry: a portal is registered
 * when it exists, and the type keeps saying so rather than being tightened to a
 * total record whose next absence would be a compile error somewhere far from
 * the decision.
 *
 * `gallery` and `verifier` joined in the order docs/accessFGARules.md predicted
 * — the remaining workspaces "get their types when they get their tables".
 * `gallery` got `ondo.consignments`; `verifier` got something slightly
 * different, and worth naming because it is not a table of its own:
 * `ondo.provenance_verification_requests` has existed since 20260804090500, and
 * what 20260902093000 added was a way to be NAMED in one without already being
 * somebody here. The relation was always the door; what was missing was anyone
 * who could only ever walk through it.
 *
 * `estate` closed the list, and INVERTED the pattern the sentence above
 * describes: its table — `works.attributed_principal_id`, readable by the
 * attributed estate since collections shipped — predates its portal by more
 * migrations than any other. The rail arrived last to the relation that had
 * waited longest, and most of its destinations are `planned` in the spec, each
 * naming the write path it still waits on.
 */
const PORTALS: Partial<Record<RoleKey, PortalSpec>> = {
  collector: COLLECTOR_PORTAL,
  agent: AGENT_PORTAL,
  gallery: GALLERY_PORTAL,
  verifier: VERIFIER_PORTAL,
  estate: ESTATE_PORTAL,
};

export function portalFor(role: RoleKey): PortalSpec | null {
  return PORTALS[role] ?? null;
}
